import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  Button,
  FormControl,
  FormLabel,
  Container,
  useToast,
  Heading,
  Text,
  useColorModeValue,
  Card,
  CardBody,
  Flex,
  Icon,
  Progress,
  Input,
  Select,
} from "@chakra-ui/react";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { storage, firestore, collection, query, where, getDocs, addDoc } from "../../firebase/firebaseConfig";
import { useAuth } from "../../context/AuthProvider";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { MdCloudUpload } from "react-icons/md";
import { PeerxLogo } from "../../components/icons/Icons";
import { useTranslation } from "react-i18next";
import { fetchKYCStatus } from "../../services/kycService";

const KYCUploadPage = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  const [idFront, setIdFront] = useState(null);
  const [idBack, setIdBack] = useState(null);
  const [selfie, setSelfie] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({
    idFront: 0,
    idBack: 0,
    selfie: 0,
  });

  const cardBgColor = useColorModeValue("brand.100", "gray.700");
  const textColor = useColorModeValue("brand.400", "white");
  const dropZoneBg = useColorModeValue("gray.100", "gray.600");

    useEffect(() => {
      const checkKYCStatus = async () => {
        if (!user?.uid) return;
  
        try {
          const kycStatus = await fetchKYCStatus(user.uid);
  
          if (kycStatus === "approved") {
            // If KYC is approved, redirect to dashboard
            navigate("/personal/dashboard");
            return;
          }
  
          if (!kycStatus || kycStatus === "rejected") {
            // If KYC is rejected or not found, stay on the page
            console.log("User KYC status is rejected or not found");
          }
        } catch (error) {
          console.error("Error fetching KYC status:", error);
          toast({
            title: t("signinpage.kyc_check_error_title"),
            description: t("signinpage.kyc_check_error_message"),
            status: "error",
            duration: 3000,
            isClosable: true,
          });
        }
      };
  
      checkKYCStatus();
    }, [user, navigate, toast, t]);

  // Handle Language Change
  const handleLanguageChange = (event) => {
    i18n.changeLanguage(event.target.value);
  };

  const handleFileChange = (event, setFile) => {
    const file = event.target.files[0];
    if (file) setFile(file);
  };

  const uploadFileWithProgress = async (file, folderPath, fileName, progressKey) => {
    if (!file) return null;

    return new Promise((resolve, reject) => {
      const storageRef = ref(storage, `${folderPath}/${fileName}`);
      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress((prev) => ({ ...prev, [progressKey]: Math.round(progress) }));
        },
        (error) => {
          console.error(t("kycuploadpage.file_upload_error"), error);
          reject(error);
        },
        async () => {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          resolve(downloadURL);
        }
      );
    });
  };

  const handleSubmit = async () => {
    if (!idFront || !idBack || !selfie) {
      toast({
        title: t("kycuploadpage.missing_files_title"),
        description: t("kycuploadpage.missing_files_message"),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);

    try {
      const [idFrontURL, idBackURL, selfieURL] = await Promise.all([
        uploadFileWithProgress(idFront, `kyc_submissions/${user.uid}/ids`, "id_front.jpg", "idFront"),
        uploadFileWithProgress(idBack, `kyc_submissions/${user.uid}/ids`, "id_back.jpg", "idBack"),
        uploadFileWithProgress(selfie, `kyc_submissions/${user.uid}/selfie`, "selfie.jpg", "selfie"),
      ]);

      await setDoc(
        doc(firestore, "kyc_submissions", user.uid),
        {
          userId: user.uid,
          idFrontURL,
          idBackURL,
          selfieURL,
          status: "pending",
          submittedAt: serverTimestamp(),
        },
        { merge: true }
      );

      // Reference to user notifications subcollection
    const userNotificationsRef = collection(firestore, "user_profiles", user.uid, "notifications");

    // Check if a KYC notification already exists to prevent duplicates
    const existingKYCQuery = query(userNotificationsRef, where("type", "==", "kyc_verification_pending"));
    const existingKYCNotifications = await getDocs(existingKYCQuery);

    if (existingKYCNotifications.empty) {
      // Add a notification for KYC validation
      await addDoc(userNotificationsRef, {
        title: "KYC Verification Pending",
        message: "Your KYC documents have been submitted and are under review. We will notify you once the verification is complete.",
        type: "kyc_verification_pending",
        alertStatus: "info",
        createdAt: new Date(),
        status: "unread",
      });

      console.log("✅ KYC notification sent.");
    } else {
      console.log("ℹ️ KYC notification already exists, skipping duplicate.");
    }


      toast({
        title: t("kycuploadpage.submit_success_title"),
        description: t("kycuploadpage.submit_success_message"),
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      navigate("/personal/dashboard");
    } catch (error) {
      console.error(t("kycuploadpage.submit_error_title"), error);
      toast({
        title: t("kycuploadpage.submit_error_title"),
        description: error.message || t("kycuploadpage.submit_error_message"),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const renderDropZone = (label, file, progressKey, setFile) => (
    <FormControl isRequired>
      <FormLabel color={textColor}>{label}</FormLabel>
      <Flex
        direction="column"
        align="center"
        justify="center"
        bg={dropZoneBg}
        border="2px dashed"
        borderColor={useColorModeValue("gray.300", "gray.500")}
        borderRadius="md"
        p={6}
        textAlign="center"
        _hover={{ bg: useColorModeValue("gray.200", "gray.700") }}
        cursor="pointer"
        onClick={() => document.getElementById(progressKey).click()}
      >
        <Icon as={MdCloudUpload} boxSize={8} color="teal.500" mb={2} />
        <Text fontSize="sm" color="gray.500">
          {t("kycuploadpage.dropzone_instructions")}
        </Text>
        {file && <Text mt={2} color={textColor} fontWeight="bold">{file.name}</Text>}
        <Input
          id={progressKey}
          type="file"
          accept="image/jpeg, image/png"
          onChange={(e) => handleFileChange(e, setFile)}
          hidden
        />
      </Flex>
      {file && <Progress value={uploadProgress[progressKey]} mt={2} size="sm" />}
    </FormControl>
  );

  return (
    <Box py={10} bg={useColorModeValue("brand.300", "gray.900")} minH="100vh">
      {/* Language Switcher */}
      <Box position="fixed" top="4" right="4" zIndex="120" p={2}>
        <Select
          value={i18n.language}
          onChange={handleLanguageChange}
          width="120px"
          bg="white"
          color="black"
          size="sm"
        >
          <option value="en">English</option>
          <option value="fr">Français</option>
          <option value="es">Español</option>
        </Select>
      </Box>

      <Container maxW="lg">
        <Heading mb="20px" size="2xl" fontFamily="Poppins" color="brand.200" fontWeight="bold" letterSpacing="wider" as={RouterLink} to="/">
          <PeerxLogo h="90px" w="160px" />
        </Heading>
        <Card bg={cardBgColor} borderRadius="lg" shadow="lg" mt="20px" p={6}>
          <CardBody>
            <VStack spacing={6}>
              <Box textAlign="center" mb={8}>
                <Heading size="lg" textStyle="mainTitle" color="brand.300">
                  {t("kycuploadpage.kyc_title")}
                </Heading>
                <Text mt={2} color={useColorModeValue("gray.600", "gray.400")}>
                  {t("kycuploadpage.kyc_description")}
                </Text>
              </Box>

              {renderDropZone(t("kycuploadpage.id_front_label"), idFront, "idFront", setIdFront)}
              {renderDropZone(t("kycuploadpage.id_back_label"), idBack, "idBack", setIdBack)}
              {renderDropZone(t("kycuploadpage.selfie_label"), selfie, "selfie", setSelfie)}
            </VStack>
          </CardBody>
        </Card>

        <Button
          colorScheme="teal"
          isLoading={loading}
          onClick={handleSubmit}
          w="full"
          mt={6}
          py={6}
          fontWeight="bold"
          fontSize="lg"
        >
          {t("kycuploadpage.submit_button")}
        </Button>
      </Container>
    </Box>
  );
};

export default KYCUploadPage;
